/**
 * EditChargeProviderModal - Modal de edição de cobrança sincronizada com provider
 *
 * Permite alterar campos editáveis de cobranças com status aguardando_pagamento/vencido.
 * Sincroniza alterações no provider (Asaas PUT /v3/payments/{id}) antes de atualizar banco local.
 *
 * Campos editáveis variam por tipo (avulsa vs parcela de installment):
 * - Avulsa: valor, vencimento, descrição, forma de pagamento, observações, juros/multa, expiração
 * - Parcela: vencimento, descrição, observações, juros/multa, expiração (sem valor e sem forma de pagamento)
 *
 * @author Módulo Financeiro
 * @version 1.6.0
 * RELEASE: 2026-08-18 v1.6.0 - Resolve o contato principal na edição para
 *                              listar veículos associados ao cliente.
 * RELEASE: 2026-03-29 v1.0.0 - Criação do modal de edição com sync provider
 * RELEASE: 2026-06-16 v1.0.1 - Aguarda hook `meios` carregar antes de resolver tipo_pagamento.
 * RELEASE: 2026-06-17 v1.1.0 - Adiciona juros/multa e prazo de expiração.
 * RELEASE: 2026-06-19 v1.2.0 - Edição de PARCELA individual com bloqueio
 *                              de valor em billingType=indefinido.
 * RELEASE: 2026-06-19 v1.3.0 - Habilita juros/multa/expiração na edição de
 *                              PARCELA individual.
 * RELEASE: 2026-06-20 v1.4.0 - REGRA DE PRODUTO. Edição de VALOR removida de
 *                              cobranças parceladas (cobrança-pai e parcelas
 *                              filhas). Campo `valor` vira read-only com aviso
 *                              e o payload preserva sempre o valor original.
 * RELEASE: 2026-06-20 v1.5.0 - Refator visual para paridade com
 *                              EditSubscriptionModal: shell premium (header
 *                              fixo + ScrollArea + footer), zonas (bloqueada
 *                              / livre / sensível), InlineNotice padrão,
 *                              fullscreen no mobile. Lógica de submit e
 *                              regras de negócio preservadas integralmente.
 */

import { FinanceiroVehiclesBlock } from '@/components/financial/FinanceiroVehiclesBlock';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Loader2,
  Pencil,
  Lock,
  FileText,
  ShieldAlert,
  CalendarDays,
  CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useFinanceiroMeiosPagamento, TIPOS_PAGAMENTO } from '@/hooks/useFinanceiroMeiosPagamento';
import { PaymentMethodIcon } from '@/components/icons/PaymentMethodIcons';
import type { FinanceiroCobranca, FinanceiroParcela } from '@/hooks/useFinanceiroCobrancas';
import { createLogger } from '@/lib/logger';
import { useCrmCustomerById } from '@/hooks/useCrmCustomers';
import {
  SubscriptionJurosSection,
  JUROS_DEFAULT,
  type JurosValue,
} from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';

const logger = createLogger('EditChargeProviderModal');

interface EditChargeProviderModalProps {
  /** Cobrança sendo editada */
  cobranca: FinanceiroCobranca | null;
  /** Parcela individual sendo editada (null = cobrança avulsa inteira) */
  parcela?: FinanceiroParcela | null;
  /** Controla abertura */
  open: boolean;
  /** Callback para fechar */
  onOpenChange: (open: boolean) => void;
}

/** Formata número em BRL para exibição read-only. */
const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

/**
 * Modal de edição de cobrança com sincronização no provider financeiro.
 * Garante que alterações são consolidadas no provider antes de atualizar banco local.
 */
export function EditChargeProviderModal({
  cobranca,
  parcela,
  open,
  onOpenChange,
}: EditChargeProviderModalProps) {
  // O documento financeiro guarda o cliente; o contato principal vem da RPC segura.
  const { data: chargeCustomer } = useCrmCustomerById(cobranca?.customer_id ?? null);
  // ── Estados do formulário ──
  const [dataVencimento, setDataVencimento] = useState('');
  const [descricao, setDescricao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [tipoPagamento, setTipoPagamento] = useState('');
  const [saving, setSaving] = useState(false);
  // v1.1.0 — juros/multa e prazo de expiração (paridade com assinatura)
  const [juros, setJuros] = useState<JurosValue>(JUROS_DEFAULT);
  const [diasExpiracao, setDiasExpiracao] = useState<number>(60);
  // v1.7.0 — snapshot inicial p/ detectar mudança real de juros/expiração.
  // Sem isso, sempre enviávamos `juros`/`dias_expiracao` no payload e a Edge
  // injetava `fine`/`interest` no PUT, fazendo o Asaas devolver HTTP 400
  // "Somente é possível alterar a multa/juros de uma cobrança pendente"
  // quando a parcela já estava vencida/enviada.
  const initialJurosRef = useRef<JurosValue>(JUROS_DEFAULT);
  const initialDiasExpiracaoRef = useRef<number>(60);

  const queryClient = useQueryClient();
  const { meios } = useFinanceiroMeiosPagamento();

  /** Se é cobrança parcelada (pai ou parcela), valor é imutável e billingType bloqueado */
  const isInstallment = cobranca?.tipo_cobranca === 'parcelada';
  const isParcelaEdit = !!parcela;

  /**
   * v1.6.0 — Regra do provider (Asaas): parcelamentos com Cartão de Crédito
   * NÃO permitem alteração individual do vencimento. Espelhamos no front
   * para evitar viagem ao provider e erro `invalid_dueDate`.
   */
  const isCartaoCreditoParcelada = isInstallment && tipoPagamento === 'cartao_credito';

  /** Valor imutável exibido na zona bloqueada (parcela ou cobrança-pai) */
  const valorImutavel = isParcelaEdit
    ? (parcela?.valor ?? 0)
    : (cobranca?.valor_total ?? 0);

  // ── Preencher formulário ao abrir ──
  // Aguarda `meios` carregar para que a resolução do tipo_pagamento via
  // cobranca.meio_pagamento_id seja correta.
  useEffect(() => {
    if (!open || !cobranca) return;
    if (meios.length === 0) return;

    if (isParcelaEdit && parcela) {
      setDataVencimento(parcela.data_vencimento || '');
    } else {
      setDataVencimento(cobranca.data_vencimento || '');
    }
    setDescricao(cobranca.descricao || '');
    setObservacoes(cobranca.observacoes || '');

    // Resolver tipo_pagamento a partir do meio de pagamento vinculado
    const meioPag = meios.find((m) => m.id === cobranca.meio_pagamento_id);
    setTipoPagamento(meioPag?.tipo_pagamento || cobranca.tipo_pagamento || '');

    // v1.1.0 — hidratar juros/multa/expiração a partir da cobrança
    const cobAny = cobranca as unknown as Record<string, unknown>;
    const jurosSrc = cobAny.juros as Record<string, unknown> | undefined;
    let hydratedJuros: JurosValue;
    if (jurosSrc && typeof jurosSrc === 'object') {
      hydratedJuros = {
        enabled: jurosSrc.enabled === true,
        multa_tipo: (jurosSrc.multa_tipo as 'percent' | 'fixed') || 'percent',
        multa_valor: Number(jurosSrc.multa_valor) || 0,
        juros_tipo: (jurosSrc.juros_tipo as 'percent' | 'fixed') || 'percent',
        juros_valor: Number(jurosSrc.juros_valor) || 0,
      };
    } else {
      hydratedJuros = JUROS_DEFAULT;
    }
    setJuros(hydratedJuros);
    const dias = Number(cobAny.dias_expiracao_pos_vencimento);
    const hydratedDias = Number.isFinite(dias) && dias > 0 ? dias : 60;
    setDiasExpiracao(hydratedDias);
    // v1.7.0 — congela snapshot inicial p/ diff no submit
    initialJurosRef.current = hydratedJuros;
    initialDiasExpiracaoRef.current = hydratedDias;
  }, [open, cobranca, parcela, isParcelaEdit, meios]);

  // ── Reset ao fechar: evita vazar state entre cobranças distintas ──
  useEffect(() => {
    if (!open) {
      setTipoPagamento('');
      setDataVencimento('');
      setDescricao('');
      setObservacoes('');
      setJuros(JUROS_DEFAULT);
      setDiasExpiracao(60);
    }
  }, [open]);

  /**
   * Salvar alterações — envia ao provider via Edge Function.
   * Fluxo: Frontend → Edge Function → Provider → Banco local → Timeline.
   */
  const handleSave = useCallback(async () => {
    if (!cobranca) return;

    // v1.2.0 — guarda defensiva: parcela paga não pode ser editada no provider
    if (isParcelaEdit && parcela?.status === 'pago') {
      toast.error('Parcela já paga não pode ser editada');
      return;
    }

    setSaving(true);

    try {
      logger.info('Enviando edição ao provider', {
        cobranca_id: cobranca.id,
        parcela_id: parcela?.id || null,
      });

      // v1.4.0: cobrança parcelada NUNCA envia novo valor ao backend.
      // Sempre preserva o valor original (cobrança-pai ou parcela filha).
      // v1.5.0: como o campo valor foi removido da UI, o valor enviado é
      // sempre o original — sem necessidade de fallback condicional.
      const valorEnviado = valorImutavel;

      // v1.7.0 — só envia juros/dias_expiracao quando realmente mudaram.
      // Antes, mandávamos sempre, e a Edge injetava fine/interest no PUT,
      // fazendo o Asaas recusar parcelas vencidas/enviadas com HTTP 400.
      const jurosMudou =
        JSON.stringify(juros) !== JSON.stringify(initialJurosRef.current);
      const diasMudou = diasExpiracao !== initialDiasExpiracaoRef.current;

      const { data: result, error: fnError } = await supabase.functions.invoke(
        'financial-charge',
        {
          body: {
            action: 'update_payment',
            cobranca_id: cobranca.id,
            conta_corrente_id: cobranca.conta_corrente_id,
            parcela_id: parcela?.id || undefined,
            valor_total: valorEnviado,
            // v1.6.0 — não envia data_vencimento quando provider bloqueia (cartão parcelado)
            data_vencimento: isCartaoCreditoParcelada ? undefined : dataVencimento,
            descricao,
            observacoes,
            tipo_pagamento: isInstallment ? undefined : tipoPagamento,
            // v1.7.0 — campos juros/expiração só vão ao payload quando o usuário
            // efetivamente alterou. Caso contrário, omitidos para não acionar
            // fine/interest no provider (que rejeita em status não-pendente).
            ...(jurosMudou ? { juros: juros.enabled ? juros : {} } : {}),
            ...(diasMudou ? { dias_expiracao: diasExpiracao } : {}),
          },
        },
      );


      if (fnError) {
        throw new Error(fnError.message || 'Erro na comunicação com o servidor');
      }

      if (!result?.success) {
        const errorMsg = result?.error || 'Erro desconhecido ao editar cobrança';
        toast.error(errorMsg, { duration: 6000 });
        logger.warn('Provider retornou erro na edição', { error: errorMsg });
        return;
      }

      // Sucesso
      if (result.no_changes) {
        toast.info('Nenhuma alteração detectada');
      } else if (result.local_only) {
        toast.success('Observações atualizadas');
      } else {
        toast.success('Cobrança atualizada com sucesso no provider');
      }

      // Invalidar caches
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });

      onOpenChange(false);
    } catch (err) {
      logger.error('Erro ao editar cobrança', { error: err });
      toast.error('Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }, [
    cobranca,
    parcela,
    valorImutavel,
    dataVencimento,
    descricao,
    observacoes,
    tipoPagamento,
    isInstallment,
    isParcelaEdit,
    isCartaoCreditoParcelada,
    juros,
    diasExpiracao,
    queryClient,
    onOpenChange,
  ]);

  if (!cobranca) return null;

  const customerName =
    (cobranca as unknown as { customer?: { name?: string } }).customer?.name || '—';

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'p-0 gap-0 overflow-hidden',
            'sm:max-w-[48rem] sm:rounded-lg',
            'max-sm:fixed max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:h-[100dvh] max-sm:max-w-none max-sm:rounded-none',
            'flex flex-col h-full sm:max-h-[90vh]',
          )}
        >
          {/* ===== HEADER FIXO ===== */}
          <DialogHeader className="px-5 py-4 border-b shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Pencil className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base flex items-center gap-2">
                  {isParcelaEdit
                    ? `Editar Parcela #${parcela?.numero_parcela}`
                    : 'Editar Cobrança'}
                  <span className="font-mono text-xs text-muted-foreground">
                    #{cobranca.id.substring(0, 8).toUpperCase()}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Alterações serão sincronizadas com o gateway de pagamento.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* ===== CONTEÚDO ROLÁVEL ===== */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-5 space-y-5">
              {/* Aviso geral de sincronização */}
              <InlineNotice variant="info" className="text-xs">
                As alterações serão enviadas ao provider financeiro. Apenas campos
                permitidos pelo gateway serão atualizados.
              </InlineNotice>

              {/* ===== ZONA BLOQUEADA ===== */}
              <section className="rounded-lg border border-dashed border-border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Campos imutáveis
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Cliente</p>
                    <p className="font-medium truncate">{customerName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Tipo</p>
                    <p className="font-medium capitalize">
                      {isInstallment ? 'Parcelada' : 'Avulsa'}
                    </p>
                  </div>
                  {isParcelaEdit && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">Nº Parcela</p>
                      <p className="font-medium">
                        {parcela?.numero_parcela}
                        {cobranca.numero_parcelas
                          ? ` / ${cobranca.numero_parcelas}`
                          : ''}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] text-muted-foreground">Valor</p>
                    <p className="font-semibold">{formatBRL(valorImutavel)}</p>
                  </div>
                </div>
                {isInstallment && (
                  <InlineNotice variant="warning" className="text-[11px] mt-2">
                    Cobranças parceladas não permitem edição de valor. Para alterar
                    o valor, cancele a cobrança e crie uma nova.
                  </InlineNotice>
                )}
              </section>

              {/* ===== ZONA LIVRE ===== */}
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Zona livre</h3>
                  <Badge variant="outline" className="text-[10px]">
                    sempre editável
                  </Badge>
                </div>

                <div>
                  <Label className="text-xs mb-1 flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    Data de Vencimento
                    {isCartaoCreditoParcelada && (
                      <Badge variant="outline" className="text-[10px] ml-1">
                        bloqueado
                      </Badge>
                    )}
                  </Label>
                  <Input
                    type="date"
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                    disabled={isCartaoCreditoParcelada}
                  />
                  {isCartaoCreditoParcelada && (
                    <InlineNotice variant="warning" className="text-[11px] mt-2">
                      Parcelamentos com Cartão de Crédito não permitem alteração
                      individual do vencimento (regra do provider).
                    </InlineNotice>
                  )}
                </div>

                <div>
                  <Label className="text-xs mb-1 block">Descrição</Label>
                  <Input
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Descrição da cobrança"
                    maxLength={200}
                  />
                </div>

                <div>
                  <Label className="text-xs mb-1 block">Observações</Label>
                  <Textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    placeholder="Observações internas (não enviado ao provider)"
                    rows={3}
                    maxLength={500}
                    className="resize-none"
                  />
                </div>
                {/* Veículos vinculados à cobrança (módulo Veículos) */}
                <FinanceiroVehiclesBlock
                  cobrancaId={cobranca?.id ?? null}
                  customerId={cobranca?.customer_id ?? null}
                  contactId={chargeCustomer?.main_contact_id ?? null}
                  editable={!isParcelaEdit}
                  enabled={open}
                />
              </section>

              <Separator />

              {/* ===== ZONA SENSÍVEL ===== */}
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  <h3 className="text-sm font-semibold">Zona sensível</h3>
                  <Badge variant="outline" className="text-[10px] border-amber-300">
                    sincroniza com provider
                  </Badge>
                </div>

                {/* Forma de pagamento — apenas cobrança avulsa */}
                {!isInstallment ? (
                  <div>
                    <Label className="text-xs mb-1 flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      Forma de Pagamento
                    </Label>
                    <Select value={tipoPagamento} onValueChange={setTipoPagamento}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS_PAGAMENTO.filter((t) =>
                          ['pix', 'boleto', 'cartao_credito', 'indefinido'].includes(t.value),
                        ).map((tipo) => {
                          const IconComponent = PaymentMethodIcon[tipo.value];
                          return (
                            <SelectItem key={tipo.value} value={tipo.value}>
                              <div className="flex items-center gap-2">
                                {IconComponent && <IconComponent className="h-4 w-4" />}
                                {tipo.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <InlineNotice variant="warning" className="text-[11px]">
                    A forma de pagamento não pode ser alterada em parcelas de
                    parcelamento (restrição do gateway).
                  </InlineNotice>
                )}

                {/* v1.3.0 — Juros/Multa + Expiração disponíveis também na edição
                    de parcela individual. O Asaas aceita fine/interest/
                    daysAfterDueDate por parcela. */}
                <SubscriptionJurosSection
                  value={juros}
                  onChange={setJuros}
                  defaultOpen={false}
                />
                <SubscriptionExpiracaoSection
                  value={diasExpiracao}
                  onChange={setDiasExpiracao}
                  defaultOpen={false}
                />
              </section>
            </div>
          </ScrollArea>

          {/* ===== FOOTER FIXO ===== */}
          <div className="p-4 border-t shrink-0 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salvar e Sincronizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
